/* Selectors over the subject layer: the subject of a chapter, the chapters and
 * delegated extras of a route, and what to read next inside it.
 */
import { PREREQ, ROUTES, TRACKS } from '../data/routes';
import { EXTRA_EDGES, NODES } from '../data/nodes';
import { byId, chapterDone, stateOf } from './progress';
import type { Cluster } from './clusters';
import type { NodeDef, Route, SavedState, Track } from '../types';

export { PREREQ, ROUTES, TRACKS };

export function trackOf(id: string): Track | null {
  return TRACKS.find((tk) => tk.chapters.includes(id)) ?? null;
}

export function chaptersOf(route: Route): NodeDef[] {
  return route.chapters.map(byId).filter((n): n is NodeDef => !!n);
}

export function declaredParents(id: string): string[] {
  return PREREQ[id] ?? [];
}

export function parentOf(id: string): string | null {
  return EXTRA_EDGES.find((e) => e[1] === id)?.[0] ?? null;
}

/* Optional stages that hang off a chapter of this route — they come with it. */
export function addOnsOf(route: Route): NodeDef[] {
  return NODES.filter((n) => n.extra && !n.bonus && route.chapters.includes(parentOf(n.id) ?? ''));
}

export function routeDone(route: Route, s: SavedState): number {
  return chaptersOf(route).filter((n) => chapterDone(n, s)).length;
}

/* The next chapter of a route a reader can actually start: unfinished and
   unlocked. Null when the route is finished or blocked. */
export function routeNext(route: Route, s: SavedState): NodeDef | null {
  return chaptersOf(route).find((n) => !chapterDone(n, s) && stateOf(n, s) === 'available') ?? null;
}

export type RouteEntry =
  | { kind: 'open'; chapter: NodeDef }
  | { kind: 'done' }
  | { kind: 'blocked'; chapter: NodeDef; by: NodeDef | null };

/* What a group of chapters offers right now: the rule both entry surfaces use,
   so a menu row and a map caption can never disagree. */
export function groupEntry(chapters: NodeDef[], s: SavedState): RouteEntry {
  const next = chapters.find((n) => !chapterDone(n, s));
  if (!next) return { kind: 'done' };
  if (stateOf(next, s) === 'available') return { kind: 'open', chapter: next };
  const by =
    declaredParents(next.id)
      .map(byId)
      .find((parent): parent is NodeDef => !!parent && !chapterDone(parent, s)) ?? null;
  return { kind: 'blocked', chapter: next, by };
}

/* What a route offers right now: from the chapter its label names onward, so
   choosing "Give the model tools" lands on tools, not on its prerequisite. */
export function routeEntry(route: Route, s: SavedState): RouteEntry {
  const chapters = chaptersOf(route);
  const from = chapters.findIndex((n) => n.id === route.entry);
  return groupEntry(from < 0 ? chapters : chapters.slice(from), s);
}

/* The same, for a map subject. */
export function clusterEntry(cluster: Cluster, s: SavedState): RouteEntry {
  return groupEntry(
    cluster.chapters.map(byId).filter((n): n is NodeDef => !!n),
    s,
  );
}

export function entryOf(route: Route): NodeDef | null {
  return byId(route.chapters[0]);
}

/* Routes whose chapters overlap a chapter — "what else does this belong to". */
export function routesWith(id: string): Route[] {
  return ROUTES.filter((r) => r.chapters.includes(id));
}
